import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useEffect, useRef, type ComponentProps, type Ref } from 'react';
import { DayPicker, getDefaultClassNames, type DayButton } from 'react-day-picker';

import { useDateLocale } from '@/shared/i18n/format';
import { cn } from '@/shared/lib/cn';

import { buttonVariants } from './button';

/**
 * Month calendar (react-day-picker 9) styled for Orbita. The locale follows the UI language;
 * weeks start on Monday for every supported language.
 */
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  components,
  ...props
}: ComponentProps<typeof DayPicker>) {
  const locale = useDateLocale();
  const defaults = getDefaultClassNames();

  return (
    <DayPicker
      locale={locale}
      weekStartsOn={1}
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      className={cn('group/calendar bg-transparent p-3 [--cell-size:--spacing(8)]', className)}
      classNames={{
        root: cn('w-fit', defaults.root),
        months: cn('relative flex flex-col gap-4 md:flex-row', defaults.months),
        month: cn('flex w-full flex-col gap-3', defaults.month),
        nav: cn('absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1', defaults.nav),
        button_previous: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
          defaults.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
          defaults.button_next,
        ),
        month_caption: cn(
          'flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)',
          defaults.month_caption,
        ),
        dropdowns: cn(
          'flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium',
          defaults.dropdowns,
        ),
        dropdown_root: cn(
          'relative rounded-md border border-input shadow-xs has-focus:ring-3 has-focus:ring-ring/25',
          defaults.dropdown_root,
        ),
        dropdown: cn('absolute inset-0 bg-popover opacity-0', defaults.dropdown),
        caption_label: cn(
          'font-medium select-none first-letter:uppercase',
          captionLayout === 'label'
            ? 'text-sm'
            : 'flex h-8 items-center gap-1 rounded-md pr-1 pl-2 text-sm [&>svg]:size-3.5 [&>svg]:text-muted-foreground',
          defaults.caption_label,
        ),
        month_grid: 'w-full border-collapse',
        weekdays: cn('flex', defaults.weekdays),
        weekday: cn(
          'flex-1 rounded-md text-[0.75rem] font-normal text-muted-foreground select-none',
          defaults.weekday,
        ),
        week: cn('mt-1 flex w-full', defaults.week),
        day: cn(
          'group/day relative aspect-square h-full w-full p-0 text-center select-none [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md',
          defaults.day,
        ),
        range_start: cn('rounded-l-md bg-brand-soft', defaults.range_start),
        range_middle: cn('rounded-none', defaults.range_middle),
        range_end: cn('rounded-r-md bg-brand-soft', defaults.range_end),
        today: cn('rounded-md', defaults.today),
        outside: cn('text-subtle-foreground aria-selected:text-subtle-foreground', defaults.outside),
        disabled: cn('text-muted-foreground opacity-50', defaults.disabled),
        hidden: cn('invisible', defaults.hidden),
        ...classNames,
      }}
      components={{
        Root: CalendarRoot,
        Chevron: CalendarChevron,
        DayButton: CalendarDayButton,
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarRoot({
  className,
  rootRef,
  ...props
}: ComponentProps<'div'> & { rootRef?: Ref<HTMLDivElement> }) {
  return <div data-slot="calendar" ref={rootRef} className={className} {...props} />;
}

function CalendarChevron({
  className,
  orientation,
}: {
  className?: string;
  size?: number;
  disabled?: boolean;
  orientation?: 'up' | 'down' | 'left' | 'right';
}) {
  const Icon =
    orientation === 'left' ? ChevronLeftIcon : orientation === 'right' ? ChevronRightIcon : ChevronDownIcon;
  return <Icon className={cn('size-4', className)} />;
}

export function CalendarDayButton({ className, day, modifiers, ...props }: ComponentProps<typeof DayButton>) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <button
      ref={ref}
      type="button"
      data-day={day.date.toLocaleDateString()}
      data-today={modifiers.today || undefined}
      data-selected-single={
        (modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle) ||
        undefined
      }
      data-range-start={modifiers.range_start || undefined}
      data-range-end={modifiers.range_end || undefined}
      data-range-middle={modifiers.range_middle || undefined}
      className={cn(
        'flex aspect-square size-auto w-full min-w-(--cell-size) items-center justify-center rounded-md text-sm leading-none font-normal tabular-nums transition-colors outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring',
        // Today is accented unless it is also selected (then the selected colors must win).
        'data-[today=true]:font-semibold [&[data-today=true]:not([data-selected-single],[data-range-start],[data-range-end])]:text-brand-text',
        'data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[selected-single=true]:hover:bg-primary/90',
        'data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-brand-soft data-[range-middle=true]:text-foreground',
        'data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground',
        className,
      )}
      {...props}
    />
  );
}
